import * as React from 'react'
import { Link } from 'react-router-dom'
import { cva } from 'class-variance-authority'
import { AlertCircle, CheckCircle2, Info, Loader2, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils.js'
import { Button } from './button.jsx'

/**
 * Feedback surfaces: Alert, EmptyState, Spinner, ScreenMessage.
 *
 * Grouped in one file because they are one idea — how the product speaks when
 * it has something other than data to show — and keeping them together is what
 * stops nine screens each inventing their own version.
 */

const alertVariants = cva(
  'flex gap-3 rounded-md border p-4 text-sm',
  {
    variants: {
      tone: {
        info: 'border-info/25 bg-info-subtle text-info-ink',
        success: 'border-success/25 bg-success-subtle text-success-ink',
        // `warn` is for a workflow state the user can act on — not a failure.
        warn: 'border-warn/30 bg-warn-subtle text-warn-ink',
        danger: 'border-danger/25 bg-danger-subtle text-danger-ink'
      }
    },
    defaultVariants: { tone: 'info' }
  }
)

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warn: TriangleAlert,
  danger: AlertCircle
}

/**
 * @param {object} props
 * @param {'info'|'success'|'warn'|'danger'} [props.tone]
 * @param {string} [props.title]
 * @param {React.ReactNode} [props.action]
 */
export function Alert ({ tone = 'info', title, action, className, children, ...props }) {
  const Icon = ICONS[tone]
  return (
    <div
      className={cn(alertVariants({ tone }), className)}
      // Failures interrupt; everything else is announced politely when the
      // user next pauses. Getting this wrong makes a screen reader talk over
      // itself on every save.
      role={tone === 'danger' ? 'alert' : 'status'}
      {...props}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className="text-sm/relaxed opacity-90">{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

/**
 * Empty state (D7).
 *
 * ── WHY THIS IS A DESIGNED COMPONENT AND NOT A STRING ───────────────────────
 * A brand-new tenant has zero clients, zero jobs, zero estimates and zero
 * invoices. The empty state is therefore not an edge case — it is the ENTIRE
 * product for the first hour of a customer's life, and the plan treats it as
 * the primary onboarding surface.
 *
 * Every empty state must name its next action. "No results" tells a contractor
 * who just paid $199 that they bought nothing; "No jobs yet — add a client, or
 * paste your form snippet and wait for your first lead" tells them what the
 * product is for.
 *
 * @param {object} props
 * @param {React.ComponentType} [props.icon]
 * @param {string} props.title
 * @param {string} [props.description]
 * @param {{label: string, onClick?: () => void, href?: string}} [props.action]
 * @param {{label: string, onClick?: () => void, href?: string}} [props.secondaryAction]
 */
export function EmptyState ({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  className,
  children
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-dashed border-line-strong',
        'bg-surface px-6 py-12 text-center animate-fade-in',
        className
      )}
    >
      {Icon && (
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-brand-subtle">
          <Icon className="size-6 text-brand" aria-hidden="true" />
        </div>
      )}
      <h3 className="text-lg font-semibold text-ink">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-sm text-sm/relaxed text-ink-muted">{description}</p>
      )}
      {children && <div className="mt-4 w-full max-w-md">{children}</div>}
      {(action || secondaryAction) && (
        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          <EmptyStateAction action={action} />
          <EmptyStateAction action={secondaryAction} variant="secondary" />
        </div>
      )}
    </div>
  )
}

/**
 * An empty state's call to action.
 *
 * `href` renders a react-router <Link>, not a bare <a>. An anchor would do a
 * full page reload — dropping the session restore, the query cache and the
 * applied branding, then rebuilding all three. On the first-run empty state,
 * which is the most important call to action in the product, that would be a
 * visible stutter at exactly the wrong moment.
 */
function EmptyStateAction ({ action, variant = 'primary' }) {
  if (!action) return null
  if (action.href) {
    return (
      <Button asChild variant={variant}>
        <Link to={action.href}>{action.label}</Link>
      </Button>
    )
  }
  return <Button variant={variant} onClick={action.onClick}>{action.label}</Button>
}

/** @param {{label?: string, className?: string}} props */
export function Spinner ({ label = 'Loading', className }) {
  return (
    <span role="status" className={cn('inline-flex items-center gap-2 text-ink-muted', className)}>
      <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  )
}

/**
 * A full-screen message for states where there is no dashboard behind the
 * message worth showing (§3.2: `no_tenant_claim`, `no_such_workspace`).
 *
 * Deliberately NOT a toast. Phase 0 spent real effort making these failures
 * loud precisely so they would not degrade into an empty-looking dashboard,
 * and this component is where that effort finally reaches a human.
 *
 * @param {object} props
 * @param {'info'|'warn'|'danger'} [props.tone]
 * @param {string} props.title
 * @param {React.ReactNode} props.children
 * @param {React.ReactNode} [props.action]
 * @param {string} [props.detail] technical cause, for whoever has to fix it
 */
export function ScreenMessage ({ tone = 'danger', title, children, action, detail }) {
  const Icon = ICONS[tone] ?? AlertCircle
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-md rounded-lg border border-line bg-surface p-8 shadow-raised animate-slide-up">
        <div
          className={cn(
            'mb-5 flex size-11 items-center justify-center rounded-full',
            tone === 'danger' && 'bg-danger-subtle text-danger',
            tone === 'warn' && 'bg-warn-subtle text-warn',
            tone === 'info' && 'bg-info-subtle text-info'
          )}
        >
          <Icon className="size-5" aria-hidden="true" />
        </div>

        <h1 className="text-xl font-semibold tracking-tight text-ink">{title}</h1>
        <div className="mt-2 space-y-3 text-sm/relaxed text-ink-muted">{children}</div>

        {detail && (
          // The cause, available but not shouted. A contractor does not need
          // it; the person they forward the screenshot to does.
          <details className="mt-5">
            <summary className="cursor-pointer text-sm text-ink-subtle hover:text-ink-muted">
              Technical details
            </summary>
            <p className="mt-2 rounded bg-surface-sunken p-3 font-mono text-xs text-ink-muted break-words">
              {detail}
            </p>
          </details>
        )}

        {action && <div className="mt-6">{action}</div>}
      </div>
    </div>
  )
}
